import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
    Alert,
    alpha,
    Box,
    Button,
    Chip,
    Container,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    Grid,
    IconButton,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Skeleton,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from "@mui/material";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import EventAvailableOutlinedIcon from "@mui/icons-material/EventAvailableOutlined";
import FactCheckOutlinedIcon from "@mui/icons-material/FactCheckOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import SendOutlinedIcon from "@mui/icons-material/SendOutlined";
import { Link, useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { getApiBaseUrl } from "../../../config/api";

const TERM_OPTIONS = ["Term 1", "Term 2", "Term 3"];

function KpiCard({ title, value, icon: Icon, color = "primary", subtitle }) {
    return (
        <Paper variant="outlined" sx={{ p: 2, height: "100%" }}>
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 0.75 }}>
                <Typography variant="caption" color="text.secondary" fontWeight={700}>
                    {title}
                </Typography>
                <Icon sx={{ color: `${color}.main`, fontSize: 22 }} />
            </Stack>
            <Typography variant="h6" fontWeight={800} color={`${color}.main`}>
                {value}
            </Typography>
            {subtitle ? (
                <Typography variant="caption" color="text.secondary">
                    {subtitle}
                </Typography>
            ) : null}
        </Paper>
    );
}

const emptyTermForm = { id: "", term: "Term 1", openDate: "", closeDate: "", note: "" };
const emptyExamForm = {
    id: "",
    date: "",
    subjectName: "",
    examName: "",
    session: "",
    startTime: "",
    endTime: "",
    room: "",
    supervisorId: "",
    note: "",
};
const emptyEventForm = { id: "", title: "", category: "School Event", date: "", audience: "all", detailsText: "" };

const SchoolCalendar = () => {
    const { currentUser } = useSelector((state) => state.user);
    const schoolId = currentUser?._id;
    const [searchParams] = useSearchParams();

    const now = new Date();
    const baseYear = now.getMonth() >= 8 ? now.getFullYear() : now.getFullYear() - 1;
    const academicYearOptions = useMemo(
        () => [`${baseYear - 1}/${baseYear}`, `${baseYear}/${baseYear + 1}`, `${baseYear + 1}/${baseYear + 2}`],
        [baseYear]
    );

    const initialEducationLevel = useMemo(() => {
        const fromQuery = String(searchParams.get("educationLevel") || "").trim();
        if (fromQuery === "Primary" || fromQuery === "Secondary") return fromQuery;
        return currentUser?.schoolType === "Primary" ? "Primary" : "Secondary";
    }, [searchParams, currentUser?.schoolType]);

    const [educationLevel, setEducationLevel] = useState(initialEducationLevel);
    const [academicYear, setAcademicYear] = useState(academicYearOptions[1] || `${baseYear}/${baseYear + 1}`);
    const [classId, setClassId] = useState("");
    const [calendarData, setCalendarData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [feedback, setFeedback] = useState("");

    const [termDialogOpen, setTermDialogOpen] = useState(false);
    const [termForm, setTermForm] = useState(emptyTermForm);

    const [examDialogOpen, setExamDialogOpen] = useState(false);
    const [examForm, setExamForm] = useState(emptyExamForm);

    const [eventDialogOpen, setEventDialogOpen] = useState(false);
    const [eventForm, setEventForm] = useState(emptyEventForm);

    const loadCalendar = async (nextClassId = classId, nextEducationLevel = educationLevel, nextAcademicYear = academicYear) => {
        if (!schoolId) return;
        setLoading(true);
        setError("");
        setFeedback("");
        try {
            const params = new URLSearchParams({
                educationLevel: nextEducationLevel,
                academicYear: nextAcademicYear,
            });
            if (nextClassId) params.set("classId", nextClassId);
            const { data } = await axios.get(`${getApiBaseUrl()}/SchoolCalendar/${schoolId}?${params.toString()}`);
            setCalendarData(data);
            const resolvedClassId = data?.filters?.classId || "";
            if (resolvedClassId !== classId) {
                setClassId(resolvedClassId);
            }
        } catch (err) {
            setError(err.response?.data?.message || "Could not load the school calendar module.");
            setCalendarData(null);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        setEducationLevel(initialEducationLevel);
    }, [initialEducationLevel]);

    useEffect(() => {
        if (!schoolId) return;
        setClassId("");
        loadCalendar("", educationLevel, academicYear);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [schoolId, educationLevel, academicYear]);

    const selectedClass = calendarData?.selectedClass;
    const termDates = Array.isArray(calendarData?.termDates) ? calendarData.termDates : [];
    const publicHolidays = Array.isArray(calendarData?.publicHolidays) ? calendarData.publicHolidays : [];
    const examTimetable = Array.isArray(calendarData?.examTimetable) ? calendarData.examTimetable : [];
    const examMeta = calendarData?.examTimetableMeta || {};
    const teacherOptions = Array.isArray(calendarData?.teacherOptions) ? calendarData.teacherOptions : [];
    const [publishingExams, setPublishingExams] = useState(false);
    const schoolEvents = Array.isArray(calendarData?.schoolEvents) ? calendarData.schoolEvents : [];
    const classOptions = Array.isArray(calendarData?.classOptions) ? calendarData.classOptions : [];

    const subjectOptions = Array.isArray(selectedClass?.subjects) ? selectedClass.subjects : [];

    const openNewTerm = () => {
        setTermForm(emptyTermForm);
        setTermDialogOpen(true);
    };

    const openEditTerm = (row) => {
        setTermForm({
            id: row.id || row._id || "",
            term: row.term || "Term 1",
            openDate: row.openDate || "",
            closeDate: row.closeDate || "",
            note: row.note || "",
        });
        setTermDialogOpen(true);
    };

    const saveTerm = async () => {
        if (!schoolId) return;
        try {
            const payload = {
                schoolId,
                academicYear,
                educationLevel,
                term: termForm.term,
                openDate: termForm.openDate,
                closeDate: termForm.closeDate,
                note: termForm.note,
            };
            if (termForm.id) {
                await axios.put(`${getApiBaseUrl()}/SchoolCalendar/item/${termForm.id}`, { ...payload, type: "term" });
            } else {
                await axios.post(`${getApiBaseUrl()}/SchoolCalendar/${schoolId}/term`, payload);
            }
            setTermDialogOpen(false);
            setFeedback("Term dates saved.");
            await loadCalendar(classId, educationLevel, academicYear);
        } catch (err) {
            setError(err.response?.data?.message || "Could not save term dates.");
        }
    };

    const deleteCalendarRow = async (id) => {
        if (!id || !window.confirm("Delete this calendar entry?")) return;
        try {
            await axios.delete(`${getApiBaseUrl()}/SchoolCalendar/item/${id}`);
            setFeedback("Entry removed.");
            await loadCalendar(classId, educationLevel, academicYear);
        } catch (err) {
            setError(err.response?.data?.message || "Could not delete entry.");
        }
    };

    const openNewExam = () => {
        if (!classId) {
            setError("Select a class before adding exam dates.");
            return;
        }
        setExamForm({ ...emptyExamForm, subjectName: subjectOptions[0]?.subName || "" });
        setExamDialogOpen(true);
    };

    const openEditExam = (row) => {
        setExamForm({
            id: row.id || row._id || "",
            date: row.date || "",
            subjectName: row.subjectName || "",
            examName: row.examName || "",
            session: row.session || "",
            startTime: row.startTime || "",
            endTime: row.endTime || "",
            room: row.room || "",
            supervisorId: row.supervisorId || "",
            note: row.note || "",
        });
        setExamDialogOpen(true);
    };

    const saveExam = async () => {
        if (!schoolId || !classId) return;
        const subject = subjectOptions.find((s) => s.subName === examForm.subjectName);
        try {
            const payload = {
                schoolId,
                academicYear,
                educationLevel,
                date: examForm.date,
                classId,
                className: selectedClass?.sclassName || "",
                subjectName: subject?.subName || String(examForm.subjectName || "").trim(),
                subjectCode: subject?.subCode || "",
                examName: examForm.examName,
                session: examForm.session,
                startTime: examForm.startTime,
                endTime: examForm.endTime,
                room: examForm.room,
                supervisorId: examForm.supervisorId,
                note: examForm.note,
            };
            if (examForm.id) {
                await axios.put(`${getApiBaseUrl()}/SchoolCalendar/item/${examForm.id}`, { ...payload, type: "exam" });
            } else {
                await axios.post(`${getApiBaseUrl()}/SchoolCalendar/${schoolId}/exam`, payload);
            }
            setExamDialogOpen(false);
            setFeedback("Exam schedule saved.");
            await loadCalendar(classId, educationLevel, academicYear);
        } catch (err) {
            setError(err.response?.data?.message || "Could not save exam schedule.");
        }
    };

    const openNewEvent = () => {
        setEventForm(emptyEventForm);
        setEventDialogOpen(true);
    };

    const openEditEvent = (row) => {
        setEventForm({
            id: row.id || row._id || "",
            title: row.title || "",
            category: row.category || "School Event",
            date: row.date || "",
            audience: row.audience || "all",
            detailsText: row.details || "",
        });
        setEventDialogOpen(true);
    };

    const publishExamTimetable = async () => {
        if (!schoolId || !classId) return;
        if (!examMeta.readyToPublish) {
            setError("Every exam row must have a supervisor before you can publish.");
            return;
        }
        if (
            !window.confirm(
                `Publish the exam timetable for ${selectedClass?.sclassName || "this class"} to all teachers and students?`
            )
        ) {
            return;
        }
        setPublishingExams(true);
        setError("");
        try {
            const { data } = await axios.post(`${getApiBaseUrl()}/SchoolCalendar/${schoolId}/exam/publish`, {
                schoolId,
                classId,
                academicYear,
                educationLevel,
            });
            setFeedback(data?.message || "Exam timetable published.");
            await loadCalendar(classId, educationLevel, academicYear);
        } catch (err) {
            setError(err.response?.data?.message || "Could not publish exam timetable.");
        } finally {
            setPublishingExams(false);
        }
    };

    const saveEvent = async () => {
        if (!schoolId) return;
        try {
            const payload = {
                schoolId,
                academicYear,
                educationLevel,
                title: eventForm.title,
                category: eventForm.category,
                date: eventForm.date,
                audience: eventForm.audience,
                detailsText: eventForm.detailsText,
            };
            if (eventForm.id) {
                await axios.put(`${getApiBaseUrl()}/SchoolCalendar/item/${eventForm.id}`, { ...payload, type: "event" });
            } else {
                await axios.post(`${getApiBaseUrl()}/SchoolCalendar/${schoolId}/event`, payload);
            }
            setEventDialogOpen(false);
            setFeedback("School event saved.");
            await loadCalendar(classId, educationLevel, academicYear);
        } catch (err) {
            setError(err.response?.data?.message || "Could not save school event.");
        }
    };

    return (
        <Box
            sx={{
                minHeight: "100%",
                background: (t) =>
                    t.palette.mode === "light"
                        ? `linear-gradient(180deg, ${alpha(t.palette.primary.main, 0.05)} 0%, ${t.palette.grey[50]} 26%)`
                        : alpha(t.palette.common.black, 0.18),
                pb: 6,
            }}
        >
            <Container maxWidth={false} sx={{ pt: { xs: 2, sm: 3 }, px: { xs: 2, sm: 2.5, md: 3 }, maxWidth: "100%", width: "100%" }}>
                <Paper
                    elevation={0}
                    sx={{
                        p: { xs: 2.5, sm: 3 },
                        mb: 3,
                        borderRadius: 3,
                        color: "common.white",
                        background: (t) =>
                            `linear-gradient(120deg, ${t.palette.primary.dark} 0%, ${t.palette.primary.main} 55%, ${alpha(
                                t.palette.info.main,
                                0.92
                            )} 100%)`,
                        boxShadow: (t) => `0 12px 40px ${alpha(t.palette.primary.main, 0.24)}`,
                    }}
                >
                    <Stack direction={{ xs: "column", md: "row" }} spacing={2} justifyContent="space-between" alignItems={{ md: "center" }}>
                        <Stack direction="row" spacing={2} alignItems="flex-start">
                            <Box
                                sx={{
                                    width: 56,
                                    height: 56,
                                    borderRadius: 2,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    bgcolor: alpha("#fff", 0.14),
                                }}
                            >
                                <CalendarMonthOutlinedIcon sx={{ fontSize: 32 }} />
                            </Box>
                            <Box>
                                <Typography variant="overline" sx={{ opacity: 0.92, letterSpacing: 1.2 }}>
                                    Primary & Secondary Planning
                                </Typography>
                                <Typography variant="h4" fontWeight={800} sx={{ lineHeight: 1.15 }}>
                                    School Calendar Module
                                </Typography>
                                <Typography variant="body2" sx={{ opacity: 0.92, maxWidth: 720, mt: 0.5 }}>
                                    Set your own term open/close dates, exam timetable, and school or PTA events. Malawi public holidays are
                                    shown automatically. View weekly class grids on the{" "}
                                    <Link to="/Admin/timetables" style={{ color: "inherit", fontWeight: 800 }}>
                                        Class timetables
                                    </Link>{" "}
                                    page.
                                </Typography>
                            </Box>
                        </Stack>
                        <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ sm: "center" }}>
                            <Button
                                component={Link}
                                to="/Admin/timetables"
                                variant="outlined"
                                sx={{
                                    borderColor: alpha("#fff", 0.8),
                                    color: "common.white",
                                    fontWeight: 700,
                                    "&:hover": { borderColor: "common.white", bgcolor: alpha("#fff", 0.12) },
                                }}
                            >
                                Class timetables
                            </Button>
                            <Button
                                variant="contained"
                                onClick={() => loadCalendar(classId, educationLevel, academicYear)}
                                sx={{
                                    bgcolor: "common.white",
                                    color: "primary.main",
                                    fontWeight: 700,
                                    "&:hover": { bgcolor: alpha("#fff", 0.92) },
                                }}
                            >
                                Refresh
                            </Button>
                        </Stack>
                    </Stack>
                </Paper>

                {feedback ? (
                    <Alert severity="success" sx={{ mb: 2 }} onClose={() => setFeedback("")}>
                        {feedback}
                    </Alert>
                ) : null}

                <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
                    <Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems={{ md: "center" }} flexWrap="wrap">
                        <FormControl size="small" sx={{ minWidth: 180 }}>
                            <InputLabel>Education Level</InputLabel>
                            <Select label="Education Level" value={educationLevel} onChange={(e) => setEducationLevel(e.target.value)}>
                                <MenuItem value="Primary">Primary</MenuItem>
                                <MenuItem value="Secondary">Secondary</MenuItem>
                            </Select>
                        </FormControl>
                        <FormControl size="small" sx={{ minWidth: 180 }}>
                            <InputLabel>Academic Year</InputLabel>
                            <Select label="Academic Year" value={academicYear} onChange={(e) => setAcademicYear(e.target.value)}>
                                {academicYearOptions.map((item) => (
                                    <MenuItem key={item} value={item}>
                                        {item}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <FormControl size="small" sx={{ minWidth: 220 }}>
                            <InputLabel>Class</InputLabel>
                            <Select label="Class" value={classId} onChange={(e) => setClassId(e.target.value)}>
                                {classOptions.map((item) => (
                                    <MenuItem key={item.id} value={item.id}>
                                        {item.sclassName}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <Button variant="contained" onClick={() => loadCalendar(classId, educationLevel, academicYear)} disabled={loading}>
                            Apply
                        </Button>
                    </Stack>
                </Paper>

                {error ? (
                    <Alert severity="warning" sx={{ mb: 3 }} onClose={() => setError("")}>
                        {error}
                    </Alert>
                ) : null}

                <Grid container spacing={2} sx={{ mb: 3 }}>
                    <Grid item xs={12} sm={6}>
                        <KpiCard
                            title="Terms configured"
                            value={termDates.length}
                            icon={EventAvailableOutlinedIcon}
                            color="success"
                            subtitle="Set open and close dates per term"
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <KpiCard
                            title="Exam focus (selected class)"
                            value={selectedClass?.examType || "—"}
                            icon={FactCheckOutlinedIcon}
                            color="warning"
                            subtitle={
                                selectedClass?.sclassName
                                    ? `${selectedClass.sclassName} · ${selectedClass.isExamClass ? "Exam class pattern" : "Add exam dates"}`
                                    : "Pick a class in filters for exam tools"
                            }
                        />
                    </Grid>
                </Grid>

                <Grid container spacing={3}>
                    <Grid item xs={12} lg={5}>
                        <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
                            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
                                <Typography variant="h6" fontWeight={700}>
                                    Term Open / Close Dates
                                </Typography>
                                <Button size="small" variant="contained" startIcon={<AddOutlinedIcon />} onClick={openNewTerm}>
                                    Add term
                                </Button>
                            </Stack>
                            {loading && !calendarData ? (
                                <Skeleton variant="rounded" width="100%" height={180} />
                            ) : termDates.length === 0 ? (
                                <Alert severity="info">No term dates yet. Add Term 1, Term 2, and Term 3 open and close dates.</Alert>
                            ) : (
                                <TableContainer>
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>Term</TableCell>
                                                <TableCell>Open</TableCell>
                                                <TableCell>Close</TableCell>
                                                <TableCell align="right">Actions</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {termDates.map((term) => (
                                                <TableRow key={term.id || term.term}>
                                                    <TableCell>
                                                        <Typography variant="body2" fontWeight={700}>
                                                            {term.term}
                                                        </Typography>
                                                        <Typography variant="caption" color="text.secondary">
                                                            {term.note || "—"}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell>{term.openLabel}</TableCell>
                                                    <TableCell>{term.closeLabel}</TableCell>
                                                    <TableCell align="right">
                                                        <IconButton size="small" onClick={() => openEditTerm(term)} aria-label="Edit term">
                                                            <EditOutlinedIcon fontSize="small" />
                                                        </IconButton>
                                                        <IconButton
                                                            size="small"
                                                            color="error"
                                                            onClick={() => deleteCalendarRow(term.id || term._id)}
                                                            aria-label="Delete term"
                                                        >
                                                            <DeleteOutlineOutlinedIcon fontSize="small" />
                                                        </IconButton>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            )}
                        </Paper>

                        <Paper variant="outlined" sx={{ p: 2 }}>
                            <Typography variant="h6" fontWeight={700} sx={{ mb: 1.5 }}>
                                Public Holidays (Malawi)
                            </Typography>
                            {calendarData?.publicHolidayNote ? (
                                <Alert severity="info" sx={{ mb: 1.5 }}>
                                    {calendarData.publicHolidayNote}
                                </Alert>
                            ) : null}
                            {loading && !calendarData ? (
                                <Skeleton variant="rounded" width="100%" height={220} />
                            ) : (
                                <Stack spacing={1}>
                                    {publicHolidays.map((holiday) => (
                                        <Paper
                                            key={`${holiday.date}-${holiday.name}`}
                                            variant="outlined"
                                            sx={{ p: 1.25, borderRadius: 2, bgcolor: (t) => alpha(t.palette.warning.main, 0.04) }}
                                        >
                                            <Stack direction="row" justifyContent="space-between" gap={2}>
                                                <Box>
                                                    <Typography variant="body2" fontWeight={700}>
                                                        {holiday.name}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        {holiday.note}
                                                    </Typography>
                                                </Box>
                                                <Chip label={holiday.dateLabel} size="small" color="warning" variant="outlined" />
                                            </Stack>
                                        </Paper>
                                    ))}
                                </Stack>
                            )}
                        </Paper>
                    </Grid>

                    <Grid item xs={12} lg={7}>
                        <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
                            <Stack
                                direction={{ xs: "column", sm: "row" }}
                                justifyContent="space-between"
                                alignItems={{ xs: "stretch", sm: "center" }}
                                spacing={1}
                                sx={{ mb: 1.5 }}
                            >
                                <Box>
                                    <Typography variant="h6" fontWeight={700}>
                                        Exam Timetable
                                    </Typography>
                                    {examMeta.publishedAt ? (
                                        <Typography variant="caption" color="success.main" display="block">
                                            Last published {new Date(examMeta.publishedAt).toLocaleString()}
                                        </Typography>
                                    ) : (
                                        <Typography variant="caption" color="text.secondary" display="block">
                                            Assign a supervisor to each exam, then publish to teachers and students.
                                        </Typography>
                                    )}
                                </Box>
                                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap justifyContent="flex-end">
                                    <Button
                                        size="small"
                                        variant="outlined"
                                        color="secondary"
                                        startIcon={<SendOutlinedIcon />}
                                        onClick={publishExamTimetable}
                                        disabled={!classId || publishingExams || examTimetable.length === 0 || !examMeta.readyToPublish}
                                    >
                                        {publishingExams ? "Publishing…" : "Publish to staff & students"}
                                    </Button>
                                    <Button size="small" variant="contained" startIcon={<AddOutlinedIcon />} onClick={openNewExam} disabled={!classId}>
                                        Add exam
                                    </Button>
                                </Stack>
                            </Stack>
                            {examTimetable.length > 0 && examMeta.missingSupervisorCount > 0 ? (
                                <Alert severity="warning" sx={{ mb: 1.5 }}>
                                    {examMeta.missingSupervisorCount} exam row(s) still need a supervisor before publishing.
                                </Alert>
                            ) : null}
                            {loading && !calendarData ? (
                                <Skeleton variant="rounded" width="100%" height={220} />
                            ) : examTimetable.length === 0 ? (
                                <Alert severity="info">No exam rows yet. Pick a class, then add each subject exam date and time.</Alert>
                            ) : (
                                <TableContainer>
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>Date</TableCell>
                                                <TableCell>Session</TableCell>
                                                <TableCell>Subject</TableCell>
                                                <TableCell>Time</TableCell>
                                                <TableCell>Room</TableCell>
                                                <TableCell>Supervisor</TableCell>
                                                <TableCell align="right">Actions</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {examTimetable.map((exam, index) => (
                                                <TableRow key={exam.id || `${exam.date}-${exam.subjectCode}-${index}`}>
                                                    <TableCell>{exam.dateLabel}</TableCell>
                                                    <TableCell>{exam.session}</TableCell>
                                                    <TableCell>
                                                        <Typography variant="body2" fontWeight={700}>
                                                            {exam.subjectName}
                                                        </Typography>
                                                        <Typography variant="caption" color="text.secondary">
                                                            {exam.examName}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell>
                                                        {exam.startTime} - {exam.endTime}
                                                    </TableCell>
                                                    <TableCell>{exam.room || "—"}</TableCell>
                                                    <TableCell>
                                                        {exam.supervisorName ? (
                                                            <Typography variant="body2" fontWeight={600}>
                                                                {exam.supervisorName}
                                                            </Typography>
                                                        ) : (
                                                            <Chip size="small" label="Required" color="warning" variant="outlined" />
                                                        )}
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        <IconButton size="small" onClick={() => openEditExam(exam)} aria-label="Edit exam">
                                                            <EditOutlinedIcon fontSize="small" />
                                                        </IconButton>
                                                        <IconButton
                                                            size="small"
                                                            color="error"
                                                            onClick={() => deleteCalendarRow(exam.id || exam._id)}
                                                            aria-label="Delete exam"
                                                        >
                                                            <DeleteOutlineOutlinedIcon fontSize="small" />
                                                        </IconButton>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            )}
                        </Paper>

                        <Paper variant="outlined" sx={{ p: 2 }}>
                            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
                                <Typography variant="h6" fontWeight={700}>
                                    School Events / PTA Meetings
                                </Typography>
                                <Stack direction="row" spacing={1} alignItems="center">
                                    <Chip icon={<GroupsOutlinedIcon />} label={`${schoolEvents.length} items`} size="small" color="secondary" />
                                    <Button size="small" variant="contained" startIcon={<AddOutlinedIcon />} onClick={openNewEvent}>
                                        Add event
                                    </Button>
                                </Stack>
                            </Stack>
                            {loading && !calendarData ? (
                                <Skeleton variant="rounded" width="100%" height={200} />
                            ) : schoolEvents.length === 0 ? (
                                <Alert severity="info">No school events yet. Add PTA meetings, assemblies, or other dated activities.</Alert>
                            ) : (
                                <Stack spacing={1.25}>
                                    {schoolEvents.map((event, index) => (
                                        <Paper
                                            key={event.id || `${event.date}-${event.title}-${index}`}
                                            variant="outlined"
                                            sx={{
                                                p: 1.25,
                                                borderRadius: 2,
                                                bgcolor: (t) =>
                                                    event.category === "PTA Meeting"
                                                        ? alpha(t.palette.secondary.main, 0.06)
                                                        : alpha(t.palette.primary.main, 0.04),
                                            }}
                                        >
                                            <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" gap={1.5}>
                                                <Box>
                                                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 0.25 }}>
                                                        <Typography variant="body2" fontWeight={700}>
                                                            {event.title}
                                                        </Typography>
                                                        <Chip
                                                            label={event.category}
                                                            size="small"
                                                            color={event.category === "PTA Meeting" ? "secondary" : "primary"}
                                                            variant="outlined"
                                                        />
                                                    </Stack>
                                                    <Typography variant="caption" color="text.secondary">
                                                        {event.details}
                                                    </Typography>
                                                </Box>
                                                <Stack direction="row" spacing={0.5} alignItems="center">
                                                    <Chip label={event.dateLabel} size="small" color="default" variant="outlined" />
                                                    <IconButton size="small" onClick={() => openEditEvent(event)} aria-label="Edit event">
                                                        <EditOutlinedIcon fontSize="small" />
                                                    </IconButton>
                                                    <IconButton
                                                        size="small"
                                                        color="error"
                                                        onClick={() => deleteCalendarRow(event.id || event._id)}
                                                        aria-label="Delete event"
                                                    >
                                                        <DeleteOutlineOutlinedIcon fontSize="small" />
                                                    </IconButton>
                                                </Stack>
                                            </Stack>
                                        </Paper>
                                    ))}
                                </Stack>
                            )}
                        </Paper>
                    </Grid>
                </Grid>
            </Container>

            <Dialog open={termDialogOpen} onClose={() => setTermDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>{termForm.id ? "Edit term dates" : "Add term dates"}</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        <FormControl fullWidth>
                            <InputLabel>Term</InputLabel>
                            <Select label="Term" value={termForm.term} onChange={(e) => setTermForm((f) => ({ ...f, term: e.target.value }))}>
                                {TERM_OPTIONS.map((t) => (
                                    <MenuItem key={t} value={t}>
                                        {t}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <TextField
                            label="Open date"
                            type="date"
                            value={termForm.openDate}
                            onChange={(e) => setTermForm((f) => ({ ...f, openDate: e.target.value }))}
                            InputLabelProps={{ shrink: true }}
                            fullWidth
                        />
                        <TextField
                            label="Close date"
                            type="date"
                            value={termForm.closeDate}
                            onChange={(e) => setTermForm((f) => ({ ...f, closeDate: e.target.value }))}
                            InputLabelProps={{ shrink: true }}
                            fullWidth
                        />
                        <TextField
                            label="Notes"
                            value={termForm.note}
                            onChange={(e) => setTermForm((f) => ({ ...f, note: e.target.value }))}
                            multiline
                            minRows={2}
                            fullWidth
                        />
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setTermDialogOpen(false)}>Cancel</Button>
                    <Button variant="contained" onClick={saveTerm}>
                        Save
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={examDialogOpen} onClose={() => setExamDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>{examForm.id ? "Edit exam" : "Add exam"}</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        <TextField
                            label="Exam date"
                            type="date"
                            value={examForm.date}
                            onChange={(e) => setExamForm((f) => ({ ...f, date: e.target.value }))}
                            InputLabelProps={{ shrink: true }}
                            fullWidth
                        />
                        <FormControl fullWidth disabled={!subjectOptions.length}>
                            <InputLabel>Subject</InputLabel>
                            <Select
                                label="Subject"
                                value={examForm.subjectName || ""}
                                onChange={(e) => setExamForm((f) => ({ ...f, subjectName: e.target.value }))}
                            >
                                {subjectOptions.map((s) => (
                                    <MenuItem key={s.subName} value={s.subName}>
                                        {s.subName}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <TextField
                            label="Exam name"
                            value={examForm.examName}
                            onChange={(e) => setExamForm((f) => ({ ...f, examName: e.target.value }))}
                            placeholder="e.g. Continuous assessment, Mock MSCE, PSLCE Paper 1"
                            fullWidth
                        />
                        <TextField
                            label="Session label"
                            value={examForm.session}
                            onChange={(e) => setExamForm((f) => ({ ...f, session: e.target.value }))}
                            placeholder="e.g. Morning session"
                            fullWidth
                        />
                        <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                            <TextField
                                label="Start time"
                                type="time"
                                value={examForm.startTime}
                                onChange={(e) => setExamForm((f) => ({ ...f, startTime: e.target.value }))}
                                InputLabelProps={{ shrink: true }}
                                fullWidth
                            />
                            <TextField
                                label="End time"
                                type="time"
                                value={examForm.endTime}
                                onChange={(e) => setExamForm((f) => ({ ...f, endTime: e.target.value }))}
                                InputLabelProps={{ shrink: true }}
                                fullWidth
                            />
                        </Stack>
                        <TextField
                            label="Room / venue"
                            value={examForm.room}
                            onChange={(e) => setExamForm((f) => ({ ...f, room: e.target.value }))}
                            fullWidth
                        />
                        <FormControl fullWidth required>
                            <InputLabel>Exam supervisor</InputLabel>
                            <Select
                                label="Exam supervisor"
                                value={examForm.supervisorId || ""}
                                onChange={(e) => setExamForm((f) => ({ ...f, supervisorId: e.target.value }))}
                            >
                                <MenuItem value="">
                                    <em>Select teacher</em>
                                </MenuItem>
                                {teacherOptions.map((teacher) => (
                                    <MenuItem key={teacher._id || teacher.id} value={teacher._id || teacher.id}>
                                        {teacher.name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <TextField
                            label="Notes"
                            value={examForm.note}
                            onChange={(e) => setExamForm((f) => ({ ...f, note: e.target.value }))}
                            multiline
                            minRows={2}
                            fullWidth
                        />
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setExamDialogOpen(false)}>Cancel</Button>
                    <Button
                        variant="contained"
                        onClick={saveExam}
                        disabled={!examForm.date || !examForm.subjectName || !examForm.supervisorId}
                    >
                        Save
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={eventDialogOpen} onClose={() => setEventDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>{eventForm.id ? "Edit school event" : "Add school event"}</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        <TextField
                            label="Title"
                            value={eventForm.title}
                            onChange={(e) => setEventForm((f) => ({ ...f, title: e.target.value }))}
                            fullWidth
                            required
                        />
                        <FormControl fullWidth>
                            <InputLabel>Category</InputLabel>
                            <Select
                                label="Category"
                                value={eventForm.category}
                                onChange={(e) => setEventForm((f) => ({ ...f, category: e.target.value }))}
                            >
                                <MenuItem value="School Event">School Event</MenuItem>
                                <MenuItem value="PTA Meeting">PTA Meeting</MenuItem>
                                <MenuItem value="Assembly">Assembly</MenuItem>
                                <MenuItem value="Sports / Culture">Sports / Culture</MenuItem>
                                <MenuItem value="Other">Other</MenuItem>
                            </Select>
                        </FormControl>
                        <TextField
                            label="Event date"
                            type="date"
                            value={eventForm.date}
                            onChange={(e) => setEventForm((f) => ({ ...f, date: e.target.value }))}
                            InputLabelProps={{ shrink: true }}
                            fullWidth
                        />
                        <FormControl fullWidth>
                            <InputLabel>Audience</InputLabel>
                            <Select
                                label="Audience"
                                value={eventForm.audience}
                                onChange={(e) => setEventForm((f) => ({ ...f, audience: e.target.value }))}
                            >
                                <MenuItem value="all">Everyone</MenuItem>
                                <MenuItem value="teacher">Teachers</MenuItem>
                                <MenuItem value="student">Students / guardians</MenuItem>
                            </Select>
                        </FormControl>
                        <TextField
                            label="Details"
                            value={eventForm.detailsText}
                            onChange={(e) => setEventForm((f) => ({ ...f, detailsText: e.target.value }))}
                            multiline
                            minRows={3}
                            fullWidth
                        />
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setEventDialogOpen(false)}>Cancel</Button>
                    <Button variant="contained" onClick={saveEvent} disabled={!eventForm.title || !eventForm.date}>
                        Save
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default SchoolCalendar;
